import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Inbox, ArrowRight, ArrowLeft, Check, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LeaOrb } from "../components/Lea";
import LanguageSwitcher from "../components/LanguageSwitcher";

const TONES = ["professionnel", "chaleureux", "direct", "commercial", "personnalisé"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    company_name: "", sector: "", website: "", description: "",
    tone: "professionnel", custom_instructions: "",
  });
  const toneLabels = t("onboarding.tones");

  const finish = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding", { ...form });
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success(t("onboarding.ready"));
      const intent = sessionStorage.getItem("elvarify-intent-plan");
      navigate(intent ? `/app/compte?subscribe=${intent}` : "/app");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const steps = [
    {
      title: t("onboarding.s1title"),
      sub: t("onboarding.s1sub"),
      content: (
        <div className="flex flex-col items-center text-center py-8">
          <LeaOrb size={72} className="mb-8" />
          <p className="text-muted-foreground text-sm max-w-sm leading-relaxed">{t("onboarding.s1body")}</p>
        </div>
      ),
    },
    {
      title: t("onboarding.s2title"),
      sub: t("onboarding.s2sub"),
      content: (
        <div className="space-y-3 py-4">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/mailboxes/oauth/gmail/start`} data-testid="connect-gmail-btn"
            className="w-full rounded-xl border border-border bg-card px-5 py-4 text-sm font-semibold flex items-center gap-3 hover:border-electric/50 transition-colors">
            <Mail size={17} className="text-red-500" /> {t("onboarding.connectGmail")}
          </a>
          <button type="button" data-testid="connect-outlook-btn" onClick={() => toast.info(t("onboarding.oauthInfoOutlook"))}
            className="w-full rounded-xl border border-border bg-card px-5 py-4 text-sm font-semibold flex items-center gap-3 hover:border-electric/50 transition-colors opacity-60">
            <Mail size={17} className="text-blue-500" /> {t("onboarding.connectOutlook")}
          </button>
          <div className="rounded-xl border border-dashed border-electric/40 bg-electric/5 px-5 py-4 text-xs text-muted-foreground leading-relaxed">
            {t("onboarding.demoNote1")} <strong className="text-foreground">{t("onboarding.demoNote2")}</strong> {t("onboarding.demoNote3")}
          </div>
        </div>
      ),
    },
    {
      title: t("onboarding.s3title"),
      sub: t("onboarding.s3sub"),
      content: (
        <div className="space-y-3 py-2">
          <input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
            placeholder={t("onboarding.company")} data-testid="onboarding-company"
            className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-electric focus:ring-2 focus:ring-electric/20 transition-shadow" />
          <input value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })}
            placeholder={t("onboarding.sector")} data-testid="onboarding-sector"
            className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-electric focus:ring-2 focus:ring-electric/20 transition-shadow" />
          <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
            placeholder={t("onboarding.website")} data-testid="onboarding-website"
            className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-electric focus:ring-2 focus:ring-electric/20 transition-shadow" />
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder={t("onboarding.description")} rows={3} data-testid="onboarding-description"
            className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-electric focus:ring-2 focus:ring-electric/20 transition-shadow resize-none" />
        </div>
      ),
    },
    {
      title: t("onboarding.s4title"),
      sub: t("onboarding.s4sub"),
      content: (
        <div className="py-2 space-y-5">
          <div className="flex flex-wrap gap-2">
            {TONES.map((tone, i) => (
              <button key={tone} type="button" onClick={() => setForm({ ...form, tone })} data-testid={`tone-${tone}`}
                className={`rounded-full px-4 py-2 text-xs font-semibold border transition-colors ${form.tone === tone ? "bg-electric text-white border-electric" : "border-border bg-card hover:border-electric/50"}`}>
                {toneLabels[i]}
              </button>
            ))}
          </div>
          <textarea value={form.custom_instructions} onChange={(e) => setForm({ ...form, custom_instructions: e.target.value })}
            rows={4} data-testid="onboarding-instructions"
            placeholder={t("onboarding.instructionsPlaceholder")}
            className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-electric focus:ring-2 focus:ring-electric/20 transition-shadow resize-none" />
        </div>
      ),
    },
    {
      title: t("onboarding.s5title"),
      sub: t("onboarding.s5sub"),
      content: (
        <div className="flex flex-col items-center text-center py-8">
          <div className="h-16 w-16 rounded-full bg-success/10 flex items-center justify-center mb-6">
            <Check size={28} className="text-success" />
          </div>
          <ul className="text-sm text-muted-foreground space-y-2.5 text-left">
            {t("onboarding.checklist").map((item) => (
              <li key={item} className="flex items-center gap-2"><Check size={14} className="text-success" /> {item}</li>
            ))}
          </ul>
        </div>
      ),
    },
  ];

  const canNext = step !== 2 || form.company_name.trim().length > 1;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 py-12" data-testid="onboarding-page">
      <div className="w-full max-w-lg flex items-center justify-between mb-10">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary dark:bg-electric flex items-center justify-center">
            <Inbox size={16} className="text-white" />
          </div>
          <span className="font-display font-bold text-lg">{t("common.appName")}</span>
        </div>
        <LanguageSwitcher />
      </div>
      <div className="flex gap-2 mb-10" data-testid="onboarding-progress">
        {steps.map((_, i) => (
          <div key={i} className={`h-1 rounded-full transition-all duration-500 ${i === step ? "w-10 bg-electric" : i < step ? "w-6 bg-electric/40" : "w-6 bg-border"}`} />
        ))}
      </div>
      <div className="w-full max-w-lg">
        <AnimatePresence mode="wait">
          <motion.div key={step}
            initial={{ opacity: 0, x: 32 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -32 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mb-2" data-testid="onboarding-title">{steps[step].title}</h1>
            <p className="text-muted-foreground text-sm mb-8">{steps[step].sub}</p>
            {steps[step].content}
          </motion.div>
        </AnimatePresence>
        <div className="flex items-center justify-between mt-8">
          <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="onboarding-back"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-0">
            <ArrowLeft size={15} /> {t("onboarding.back")}
          </button>
          {step < steps.length - 1 ? (
            <button onClick={() => canNext && setStep((s) => s + 1)} disabled={!canNext} data-testid="onboarding-next"
              className="rounded-full bg-electric text-white px-7 py-3 text-sm font-bold hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2">
              {t("onboarding.next")} <ArrowRight size={15} />
            </button>
          ) : (
            <button onClick={finish} disabled={busy} data-testid="onboarding-finish"
              className="rounded-full bg-electric text-white px-7 py-3 text-sm font-bold hover:bg-blue-700 transition-colors disabled:opacity-60 flex items-center gap-2">
              {busy ? t("onboarding.analyzing") : t("onboarding.finish")} <ArrowRight size={15} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
